import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import { getPlanLimitsForTier } from '../../../utils/planLimits';

const InputField = ({ label, value, onChange, type = 'text', required = false, placeholder = '' }) => (
  <div>
    <label className="block text-xs text-white/40 font-medium mb-1.5">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete={type === 'password' ? 'new-password' : 'off'}
      className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none transition-all"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
      onFocus={(e) => { e.target.style.border = '1px solid rgba(254,131,56,0.4)'; }}
      onBlur={(e) => { e.target.style.border = '1px solid rgba(255,255,255,0.1)'; }}
      onWheel={type === 'number' ? (e) => e.target.blur() : undefined}
    />
  </div>
);

const validatePassword = (password) => {
  return password?.length >= 8 && /[A-Z]/.test(password) && /[a-z]/.test(password) && /[0-9]/.test(password);
};

const generatePassword = () => {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const all = upper + lower + digits;
  let pwd = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
  ];
  for (let i = 3; i < 12; i++) pwd.push(all[Math.floor(Math.random() * all.length)]);
  return pwd.sort(() => Math.random() - 0.5).join('');
};

const sanitizeUsername = (raw) => raw.toLowerCase().replace(/[^a-z0-9._]/g, '');
const isUsernameValid = (u) => u && u.length >= 6 && /^[a-z0-9._]+$/.test(u);

const parseUsername = (username) => {
  const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
  const stripNums = (str) => str ? str.replace(/[0-9]/g, '') : str;
  const match = username.match(/[._]/);
  if (!match) {
    const name = stripNums(username);
    return { first_name: name ? capitalize(name) : null, last_name: null };
  }
  const rawFirst = stripNums(username.substring(0, match.index));
  const first_name = rawFirst ? capitalize(rawFirst) : null;
  const rest = username.substring(match.index + 1).replace(/^[._]+/, '');
  const lastMatch = rest.match(/^([^._]+)/);
  const rawLast = lastMatch ? stripNums(lastMatch[1]) : null;
  const last_name = rawLast ? capitalize(rawLast) : null;
  return { first_name: first_name || null, last_name: last_name || null };
};

const TeamSection = () => {
  const { user, userProfile } = useAuth();
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [ownerProjectCount, setOwnerProjectCount] = useState(0);
  const [ownerSaveCount, setOwnerSaveCount] = useState(0);
  const [ownerMaxProjects, setOwnerMaxProjects] = useState(null);
  const [ownerMaxSaves, setOwnerMaxSaves] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [editUsernameError, setEditUsernameError] = useState('');
  const actionMenuRef = useRef(null);

  // Add member form
  const [addForm, setAddForm] = useState({
    username: '', password: '', confirmPassword: '', firstName: '', lastName: '', monthlyCredits: '', maxProjects: '', maxStorage: '',
  });

  // Edit member form
  const [editForm, setEditForm] = useState({
    firstName: '', lastName: '', username: '', password: '', confirmPassword: '', monthlyCredits: '', maxProjects: '', maxStorage: '',
  });

  // Close action menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setActionMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch team members
  const fetchTeamMembers = async () => {
    if (!user?.id) return;
    try {
      setIsLoading(true);
      const { data, error: fetchErr } = await supabase
        .from('team_members')
        .select('id, role, created_at, member_user_id, max_projects, max_storage, user_profiles!team_members_member_user_id_fkey(id, email, first_name, last_name, username, avatar_url, remaining_credits, monthly_credits, created_at, status)')
        .eq('team_owner_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (fetchErr) throw fetchErr;

      // Fetch project & storage counts per member
      const { data: usageCounts } = await supabase.rpc('get_team_members_usage_counts');
      const countsMap = {};
      (usageCounts || []).forEach((row) => {
        countsMap[row.member_user_id] = { projectCount: row.project_count || 0, saveCount: row.save_design_count || 0 };
      });

      // Fetch upload counts per member
      const memberIds = (data || []).map((row) => row.user_profiles?.id).filter(Boolean);
      const uploadResults = await Promise.all(
        memberIds.map((mid) => supabase.rpc('count_uploaded_media', { target_user_id: mid }))
      );
      const uploadMap = {};
      memberIds.forEach((mid, i) => { uploadMap[mid] = uploadResults[i]?.data ?? 0; });

      const members = (data || []).map((row) => ({
        ...row.user_profiles,
        teamRole: row.role,
        teamMemberId: row.id,
        teamCreatedAt: row.created_at,
        max_projects: row.max_projects,
        max_storage: row.max_storage,
        projectCount: countsMap[row.user_profiles?.id]?.projectCount || 0,
        saveCount: (countsMap[row.user_profiles?.id]?.saveCount || 0) + (uploadMap[row.user_profiles?.id] || 0),
      }));

      setTeamMembers(members);
    } catch (err) {
      console.error('Error fetching team members:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || userProfile?.subscription_tier !== 'team') return;
    const fetchOwnerUsage = async () => {
      const [projectsRes, savesRes, uploadRes, limits] = await Promise.all([
        supabase.rpc('get_team_total_project_count'),
        supabase.rpc('get_team_total_save_design_count'),
        supabase.rpc('count_uploaded_media', { target_user_id: user.id }),
        getPlanLimitsForTier('team', user.id),
      ]);
      setOwnerProjectCount(projectsRes.data ?? 0);
      setOwnerSaveCount((savesRes.data ?? 0) + (uploadRes.data ?? 0));
      setOwnerMaxProjects(limits.maxProjects);
      setOwnerMaxSaves(limits.maxStorage);
    };
    fetchOwnerUsage();
  }, [user?.id, userProfile?.subscription_tier]);

  // Team stats — members share owner's credit pool
  const ownerCreditsPool = userProfile?.monthly_credits || 0;
  const ownerRemaining = userProfile?.remaining_credits || 0;
  const totalPoolUsed = ownerCreditsPool - ownerRemaining; // total used from pool (owner + members)
  const totalMemberUsed = teamMembers.reduce((sum, m) => sum + ((m.monthly_credits || 0) - (m.remaining_credits || 0)), 0);
  const ownerPersonalUsed = Math.max(0, totalPoolUsed - totalMemberUsed); // owner's own usage only

  const totalCreditsUsed = totalPoolUsed;
  const totalCreditsPool = ownerCreditsPool;
  const totalRemaining = ownerRemaining;
  const totalMemberCount = teamMembers.length + 1; // +1 for owner

  const getMemberName = (member) => {
    const name = `${member?.first_name ?? ''} ${member?.last_name ?? ''}`.trim();
    return name || member?.username || 'No Name';
  };

  const getMemberInitials = (member) => {
    if (member?.first_name && member?.last_name) return `${member.first_name[0]}${member.last_name[0]}`.toUpperCase();
    if (member?.first_name) return member.first_name[0].toUpperCase();
    if (member?.username) return member.username[0].toUpperCase();
    return 'U';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const subTabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'members', label: 'Members', icon: 'Users' },
    { id: 'usage', label: 'Usage', icon: 'BarChart3' },
  ];

  // ── Edge Function call helper ──
  const callManageTeamMember = async (body) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-team-member`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(body),
      }
    );

    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Request failed');
    return result;
  };

  // ── Validate member limits against owner's plan ──
  const validateMemberLimits = (credits, projects, saves) => {
    const ownerPool = userProfile?.monthly_credits ?? 0;
    if (credits > ownerPool) {
      return `Max Credits (${credits}) cannot exceed your plan limit (${ownerPool})`;
    }
    if (ownerMaxProjects !== null && projects !== null && projects > ownerMaxProjects) {
      return `Max Projects (${projects}) cannot exceed your plan limit (${ownerMaxProjects})`;
    }
    if (ownerMaxSaves !== null && saves !== null && saves > ownerMaxSaves) {
      return `Max Storage (${saves}) cannot exceed your plan limit (${ownerMaxSaves})`;
    }
    return null;
  };

  // ── Add Member ──
  const handleAddMember = async () => {
    const rawUsername = addForm.username.trim().toLowerCase();
    if (!rawUsername || rawUsername.length < 6) {
      setUsernameError('Username must be at least 6 characters');
      return;
    }
    if (!/^[a-z0-9._]+$/.test(rawUsername)) {
      setUsernameError('Only a-z, 0-9, . and _ are allowed');
      return;
    }
    setAddForm(prev => ({ ...prev, username: rawUsername }));
    setSaving(true);
    setError('');
    try {
      const uname = rawUsername;
      const parsed = parseUsername(uname);
      const credits = parseInt(addForm.monthlyCredits, 10) || 0;
      const projects = addForm.maxProjects ? parseInt(addForm.maxProjects, 10) : null;
      const saves = addForm.maxStorage ? parseInt(addForm.maxStorage, 10) : null;
      const limitErr = validateMemberLimits(credits, projects, saves);
      if (limitErr) { setError(limitErr); setSaving(false); return; }
      await callManageTeamMember({
        action: 'create',
        username: uname,
        password: addForm.password,
        firstName: addForm.firstName.trim() || parsed.first_name || uname,
        lastName: addForm.lastName.trim() || parsed.last_name || '',
        monthlyCredits: parseInt(addForm.monthlyCredits, 10) || 0,
        maxProjects: addForm.maxProjects ? parseInt(addForm.maxProjects, 10) : null,
        maxStorage: addForm.maxStorage ? parseInt(addForm.maxStorage, 10) : null,
      });
      setShowAddModal(false);
      setAddForm({ username: '', password: '', confirmPassword: '', firstName: '', lastName: '', monthlyCredits: '', maxProjects: '', maxStorage: '' });
      await fetchTeamMembers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Edit Member ──
  const openEditModal = (member) => {
    setSelectedMember(member);
    setEditForm({
      firstName: member.first_name || '',
      lastName: member.last_name || '',
      username: member.username || '',
      password: '',
      confirmPassword: '',
      monthlyCredits: String(member.monthly_credits || 0),
      maxProjects: member.max_projects != null ? String(member.max_projects) : '',
      maxStorage: member.max_storage != null ? String(member.max_storage) : '',
    });
    setActionMenuId(null);
    setShowEditModal(true);
    setError('');
  };

  const handleEditMember = async () => {
    if (!selectedMember) return;
    const rawUsername = editForm.username.trim().toLowerCase();
    if (rawUsername && rawUsername.length < 6) {
      setEditUsernameError('Username must be at least 6 characters');
      return;
    }
    if (rawUsername && !/^[a-z0-9._]+$/.test(rawUsername)) {
      setEditUsernameError('Only a-z, 0-9, . and _ are allowed');
      return;
    }
    setEditForm(prev => ({ ...prev, username: rawUsername }));
    setSaving(true);
    setError('');
    try {
      const credits = parseInt(editForm.monthlyCredits, 10) || 0;
      const projects = editForm.maxProjects ? parseInt(editForm.maxProjects, 10) : null;
      const saves = editForm.maxStorage ? parseInt(editForm.maxStorage, 10) : null;
      const limitErr = validateMemberLimits(credits, projects, saves);
      if (limitErr) { setError(limitErr); setSaving(false); return; }
      const payload = {
        action: 'update',
        memberId: selectedMember.id,
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        username: rawUsername,
        monthlyCredits: credits,
        maxProjects: projects,
        maxStorage: saves,
      };
      if (editForm.password) payload.password = editForm.password;

      await callManageTeamMember(payload);
      setShowEditModal(false);
      setSelectedMember(null);
      await fetchTeamMembers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Delete Member ──
  const openDeleteModal = (member) => {
    setSelectedMember(member);
    setActionMenuId(null);
    setShowDeleteModal(true);
    setError('');
  };

  const handleDeleteMember = async () => {
    if (!selectedMember) return;
    setSaving(true);
    setError('');
    try {
      await callManageTeamMember({ action: 'delete', memberId: selectedMember.id });
      setShowDeleteModal(false);
      setSelectedMember(null);
      await fetchTeamMembers();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };


  // ── Owner row for member lists ──
  const OwnerRow = ({ compact = false }) => {
    return (
      <div className={`flex items-center gap-3 ${compact ? 'p-3 rounded-xl' : 'px-5 py-4'}`}>
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ background: 'rgba(254, 131, 56, 0.15)' }}>
          {userProfile?.avatar_url ? (
            <img src={userProfile.avatar_url} alt="You" className="w-full h-full object-cover rounded-full" />
          ) : (
            <span className="text-xs font-semibold text-orange-400">
              {(userProfile?.first_name?.[0] || 'Y').toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-white truncate">
              {`${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() || 'You'}
              <span className="text-xs text-orange-400 ml-1">(You)</span>
            </p>
            <span className="flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full text-yellow-400" style={{ background: 'rgba(234, 179, 8, 0.12)', border: '1px solid rgba(234, 179, 8, 0.25)' }}>
              Owner
            </span>
          </div>
          {!compact && <p className="text-xs text-white/30 truncate">@{userProfile?.username || '—'}</p>}
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-medium text-white">{ownerRemaining} / {userProfile?.monthly_credits || 0}</p>
          <p className="text-[10px] text-white/30">credits</p>
        </div>
      </div>
    );
  };

  // Not a team owner — show fallback
  if (userProfile?.subscription_tier !== 'team') {
    return (
      <div className="glassmorphic-card rounded-2xl p-6 md:p-8 flex flex-col items-center justify-center py-16">
        <Icon name="Lock" size={40} className="text-white/20 mb-3" />
        <p className="text-white/50 text-sm">Team Management is only available for Team plan subscribers.</p>
      </div>
    );
  }

  return (
    <div className="glassmorphic-card rounded-2xl p-6 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="UsersRound" size={22} className="text-orange-400" />
          <h2 className="text-xl font-bold text-white">Team Dashboard</h2>
        </div>
        <p className="text-sm text-white/40">Manage your team and monitor usage</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 mb-6 p-1 rounded-xl" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 flex-1 justify-center ${
              activeSubTab === tab.id ? 'text-orange-400' : 'text-white/50 hover:text-white/80'
            }`}
            style={activeSubTab === tab.id ? { background: 'rgba(254, 131, 56, 0.1)', boxShadow: '0 0 12px rgba(254, 131, 56, 0.08)' } : {}}
          >
            <Icon name={tab.icon} size={16} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-orange-400 mb-3" />
          <p className="text-sm text-white/40">Loading team data...</p>
        </div>
      )}

      {/* ── Overview Tab ── */}
      {!isLoading && activeSubTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(254, 131, 56, 0.15)' }}>
                  <Icon name="Users" size={18} className="text-orange-400" />
                </div>
                <span className="text-xs text-white/40 font-medium">Team Members</span>
              </div>
              <p className="text-2xl font-bold text-white">{totalMemberCount}</p>
            </div>
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(34, 197, 94, 0.15)' }}>
                  <Icon name="Zap" size={18} className="text-green-400" />
                </div>
                <span className="text-xs text-white/40 font-medium">Credits Used</span>
              </div>
              <p className="text-2xl font-bold text-white">{totalCreditsUsed.toLocaleString()}</p>
              <p className="text-xs text-white/30 mt-1">of {totalCreditsPool.toLocaleString()} total</p>
            </div>
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(234, 179, 8, 0.15)' }}>
                  <Icon name="Coins" size={18} className="text-yellow-400" />
                </div>
                <span className="text-xs text-white/40 font-medium">Credits Remaining</span>
              </div>
              <p className="text-2xl font-bold text-white">{totalRemaining.toLocaleString()}</p>
              <p className="text-xs text-white/30 mt-1">
                {totalCreditsPool > 0 ? `${Math.round((totalRemaining / totalCreditsPool) * 100)}% available` : '0% available'}
              </p>
            </div>
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(99, 102, 241, 0.15)' }}>
                  <Icon name="FolderOpen" size={18} className="text-indigo-400" />
                </div>
                <span className="text-xs text-white/40 font-medium">Projects</span>
              </div>
              <p className="text-2xl font-bold text-white">{ownerProjectCount}</p>
              <p className="text-xs text-white/30 mt-1">of {ownerMaxProjects != null ? ownerMaxProjects : '∞'} max</p>
            </div>
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(168, 85, 247, 0.15)' }}>
                  <Icon name="BookImage" size={18} className="text-purple-400" />
                </div>
                <span className="text-xs text-white/40 font-medium">Storage</span>
              </div>
              <p className="text-2xl font-bold text-white">{ownerSaveCount}</p>
              <p className="text-xs text-white/30 mt-1">of {ownerMaxSaves != null ? ownerMaxSaves : '∞'} max</p>
            </div>
          </div>

          {/* Members Quick View */}
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <Icon name="Users" size={18} className="text-orange-400" />
              Team Members
            </h3>
            <div className="space-y-2">
              <OwnerRow compact />
              {teamMembers.slice(0, 5).map((member) => (
                <div key={member.teamMemberId ?? member.id} className="flex items-center gap-3 p-3 rounded-xl transition-all duration-300 hover:bg-white/[0.03]">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ background: 'rgba(254, 131, 56, 0.15)' }}>
                    {member.avatar_url ? (
                      <img src={member.avatar_url} alt={getMemberName(member)} className="w-full h-full object-cover rounded-full" />
                    ) : (
                      <span className="text-xs font-semibold text-orange-400">{getMemberInitials(member)}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{getMemberName(member)}</p>
                    <p className="text-xs text-white/30 truncate">@{member.username || '—'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-medium text-white">{member.remaining_credits || 0} / {member.monthly_credits || 0}</p>
                    <p className="text-[10px] text-white/30">credits</p>
                  </div>
                </div>
              ))}
              {teamMembers.length > 5 && (
                <button onClick={() => setActiveSubTab('members')} className="w-full text-center text-sm text-orange-400 hover:text-orange-300 py-2 transition-colors">
                  View all {teamMembers.length} members
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Members Tab ── */}
      {!isLoading && activeSubTab === 'members' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => { setShowAddModal(true); setError(''); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-all duration-300 hover:scale-[1.02]"
              style={{ background: 'rgba(254, 131, 56, 0.15)', border: '1px solid rgba(254, 131, 56, 0.3)' }}
            >
              <Icon name="UserPlus" size={16} className="text-orange-400" />
              <span className="text-orange-400">Add Member</span>
            </button>
          </div>

          <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
            <div className="overflow-x-auto overflow-y-auto max-h-[400px] orange-scrollbar">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
                    <th className="text-left px-5 py-3.5 text-xs font-medium text-white/40 uppercase tracking-wider">Member</th>
                    <th className="text-center px-5 py-3.5 text-xs font-medium text-white/40 uppercase tracking-wider">Credits Used</th>
                    <th className="text-center px-5 py-3.5 text-xs font-medium text-white/40 uppercase tracking-wider">Max Credits</th>
                    <th className="text-center px-5 py-3.5 text-xs font-medium text-white/40 uppercase tracking-wider">Projects</th>
                    <th className="text-center px-5 py-3.5 text-xs font-medium text-white/40 uppercase tracking-wider">Storage</th>
                    <th className="text-center px-5 py-3.5 text-xs font-medium text-white/40 uppercase tracking-wider">Joined</th>
                    <th className="text-center px-5 py-3.5 text-xs font-medium text-white/40 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Owner row */}
                  <tr className="hover:bg-white/[0.02]" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ background: 'rgba(254, 131, 56, 0.15)' }}>
                          <span className="text-[10px] font-semibold text-orange-400">{(userProfile?.first_name?.[0] || 'Y').toUpperCase()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white">
                            {`${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() || 'You'}
                            <span className="text-xs text-orange-400 ml-1">(You)</span>
                          </span>
                          <span className="flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full text-yellow-400" style={{ background: 'rgba(234, 179, 8, 0.12)', border: '1px solid rgba(234, 179, 8, 0.25)' }}>Owner</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-white font-medium text-center">{ownerPersonalUsed}</td>
                    <td className="px-5 py-4 text-sm text-white font-medium text-center">{userProfile?.monthly_credits || 0}</td>
                    <td className="px-5 py-4 text-sm text-white font-medium text-center">{ownerProjectCount} / {ownerMaxProjects != null ? ownerMaxProjects : '∞'}</td>
                    <td className="px-5 py-4 text-sm text-white font-medium text-center">{ownerSaveCount} / {ownerMaxSaves != null ? ownerMaxSaves : '∞'}</td>
                    <td className="px-5 py-4 text-sm text-white/40 text-center">{formatDate(userProfile?.created_at)}</td>
                    <td className="px-5 py-4 text-center"><span className="text-xs text-white/20">—</span></td>
                  </tr>

                  {/* Member rows */}
                  {teamMembers.map((member) => {
                    const used = (member.monthly_credits || 0) - (member.remaining_credits || 0);
                    return (
                      <tr key={member.teamMemberId ?? member.id} className="transition-all duration-300 hover:bg-white/[0.02]" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ background: 'rgba(254, 131, 56, 0.15)' }}>
                              {member.avatar_url ? (
                                <img src={member.avatar_url} alt={getMemberName(member)} className="w-full h-full object-cover rounded-full" />
                              ) : (
                                <span className="text-[10px] font-semibold text-orange-400">{getMemberInitials(member)}</span>
                              )}
                            </div>
                            <div>
                              <span className="text-sm font-medium text-white">{getMemberName(member)}</span>
                              <p className="text-xs text-white/30">@{member.username || '—'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-white font-medium text-center">{used}</td>
                        <td className="px-5 py-4 text-sm text-white font-medium text-center">{member.monthly_credits || 0}</td>
                        <td className="px-5 py-4 text-sm text-white font-medium text-center">{member.projectCount} / {member.max_projects != null ? member.max_projects : '∞'}</td>
                        <td className="px-5 py-4 text-sm text-white font-medium text-center">{member.saveCount} / {member.max_storage != null ? member.max_storage : '∞'}</td>
                        <td className="px-5 py-4 text-sm text-white/40 text-center">{formatDate(member.created_at)}</td>
                        <td className="px-5 py-4 text-center relative">
                          <button
                            onClick={() => setActionMenuId(actionMenuId === member.id ? null : member.id)}
                            className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.05] transition-all duration-200 mx-auto"
                          >
                            <Icon name="MoreVertical" size={16} />
                          </button>
                          {actionMenuId === member.id && (
                            <div
                              ref={actionMenuRef}
                              className="absolute right-4 top-full mt-1 z-50 w-40 rounded-xl overflow-hidden shadow-2xl"
                              style={{ background: 'rgba(0,0,0,0.95)', border: '1px solid rgba(255,255,255,0.1)' }}
                            >
                              <button
                                onClick={() => openEditModal(member)}
                                className="w-full text-left px-4 py-2.5 text-xs text-white/70 hover:bg-white/5 hover:text-white transition-all flex items-center gap-2"
                              >
                                <Icon name="Pencil" size={14} />
                                Edit Member
                              </button>
                              <button
                                onClick={() => openDeleteModal(member)}
                                className="w-full text-left px-4 py-2.5 text-xs text-red-400 hover:bg-red-500/10 transition-all flex items-center gap-2"
                              >
                                <Icon name="Trash2" size={14} />
                                Delete Member
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {teamMembers.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-5 py-12 text-center">
                        <Icon name="Users" size={32} className="text-white/20 mx-auto mb-2" />
                        <p className="text-sm text-white/40">No team members yet. Click "Add Member" to get started.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Usage Tab ── */}
      {!isLoading && activeSubTab === 'usage' && (
        <div className="space-y-6">
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
            <div className="flex items-center gap-2 mb-5">
              <Icon name="BarChart3" size={18} className="text-orange-400" />
              <h3 className="text-base font-semibold text-white">Team Usage Summary</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-xs text-white/40 font-medium mb-2">Credits</p>
                <p className="text-2xl font-bold text-white mb-3">{totalCreditsUsed.toLocaleString()} <span className="text-sm text-white/30 font-normal">/ {totalCreditsPool.toLocaleString()}</span></p>
                <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.06)' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${totalCreditsPool > 0 ? Math.round((totalCreditsUsed / totalCreditsPool) * 100) : 0}%`, background: 'linear-gradient(90deg, rgba(254, 131, 56, 0.8), rgba(254, 131, 56, 0.6))' }} />
                </div>
                <p className="text-xs text-white/30 mt-1.5">{totalCreditsPool > 0 ? Math.round((totalCreditsUsed / totalCreditsPool) * 100) : 0}% used</p>
              </div>
              <div>
                <p className="text-xs text-white/40 font-medium mb-2">Projects</p>
                <p className="text-2xl font-bold text-white mb-3">{ownerProjectCount} <span className="text-sm text-white/30 font-normal">/ {ownerMaxProjects != null ? ownerMaxProjects : '∞'}</span></p>
                <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.06)' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${ownerMaxProjects ? Math.min(100, Math.round((ownerProjectCount / ownerMaxProjects) * 100)) : 0}%`, background: 'linear-gradient(90deg, rgba(99, 102, 241, 0.8), rgba(99, 102, 241, 0.6))' }} />
                </div>
                <p className="text-xs text-white/30 mt-1.5">{ownerMaxProjects ? `${Math.min(100, Math.round((ownerProjectCount / ownerMaxProjects) * 100))}% used` : 'Unlimited'}</p>
              </div>
              <div>
                <p className="text-xs text-white/40 font-medium mb-2">Storage</p>
                <p className="text-2xl font-bold text-white mb-3">{ownerSaveCount} <span className="text-sm text-white/30 font-normal">/ {ownerMaxSaves != null ? ownerMaxSaves : '∞'}</span></p>
                <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.06)' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${ownerMaxSaves ? Math.min(100, Math.round((ownerSaveCount / ownerMaxSaves) * 100)) : 0}%`, background: 'linear-gradient(90deg, rgba(168, 85, 247, 0.8), rgba(168, 85, 247, 0.6))' }} />
                </div>
                <p className="text-xs text-white/30 mt-1.5">{ownerMaxSaves ? `${Math.min(100, Math.round((ownerSaveCount / ownerMaxSaves) * 100))}% used` : 'Unlimited'}</p>
              </div>
            </div>
          </div>

          {/* Per-member usage */}
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
            <div className="flex items-center gap-2 mb-5">
              <Icon name="Users" size={18} className="text-orange-400" />
              <h3 className="text-base font-semibold text-white">Usage by Member</h3>
            </div>
            <div className="space-y-4">
              {/* Owner usage */}
              {(() => {
                const ownerUsagePercent = ownerCreditsPool > 0 ? Math.round((ownerPersonalUsed / ownerCreditsPool) * 100) : 0;
                const ownerProjPercent = ownerMaxProjects ? Math.min(100, Math.round((ownerProjectCount / ownerMaxProjects) * 100)) : 0;
                const ownerSavePercent = ownerMaxSaves ? Math.min(100, Math.round((ownerSaveCount / ownerMaxSaves) * 100)) : 0;
                return (
                  <div className="rounded-xl p-4" style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.04)' }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(254, 131, 56, 0.15)' }}>
                        <span className="text-[10px] font-semibold text-orange-400">{(userProfile?.first_name?.[0] || 'Y').toUpperCase()}</span>
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {`${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() || 'You'}
                          <span className="text-xs text-orange-400 ml-1">(You)</span>
                        </p>
                        <span className="flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full text-yellow-400" style={{ background: 'rgba(234, 179, 8, 0.12)', border: '1px solid rgba(234, 179, 8, 0.25)' }}>Owner</span>
                      </div>
                    </div>
                    <div className="space-y-2.5 pl-11">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-white/40">Credits</p>
                          <p className="text-xs text-white/40">{ownerPersonalUsed} / {ownerCreditsPool}</p>
                        </div>
                        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.06)' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${ownerUsagePercent}%`, background: ownerUsagePercent > 80 ? 'rgba(239, 68, 68, 0.8)' : ownerUsagePercent > 50 ? 'rgba(245, 158, 11, 0.8)' : 'rgba(34, 197, 94, 0.8)' }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-white/40">Projects</p>
                          <p className="text-xs text-white/40">{ownerProjectCount} / {ownerMaxProjects != null ? ownerMaxProjects : '∞'}</p>
                        </div>
                        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.06)' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${ownerProjPercent}%`, background: 'rgba(99, 102, 241, 0.8)' }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-white/40">Storage</p>
                          <p className="text-xs text-white/40">{ownerSaveCount} / {ownerMaxSaves != null ? ownerMaxSaves : '∞'}</p>
                        </div>
                        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.06)' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${ownerSavePercent}%`, background: 'rgba(168, 85, 247, 0.8)' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {teamMembers.map((member) => {
                const used = (member.monthly_credits || 0) - (member.remaining_credits || 0);
                const creditPercent = member.monthly_credits > 0 ? Math.round((used / member.monthly_credits) * 100) : 0;
                const projPercent = member.max_projects ? Math.min(100, Math.round((member.projectCount / member.max_projects) * 100)) : 0;
                const savePercent = member.max_storage ? Math.min(100, Math.round((member.saveCount / member.max_storage) * 100)) : 0;
                return (
                  <div key={member.teamMemberId ?? member.id} className="rounded-xl p-4" style={{ background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.04)' }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ background: 'rgba(254, 131, 56, 0.15)' }}>
                        {member.avatar_url ? (
                          <img src={member.avatar_url} alt={getMemberName(member)} className="w-full h-full object-cover rounded-full" />
                        ) : (
                          <span className="text-[10px] font-semibold text-orange-400">{getMemberInitials(member)}</span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-white truncate">{getMemberName(member)}</p>
                    </div>
                    <div className="space-y-2.5 pl-11">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-white/40">Credits</p>
                          <p className="text-xs text-white/40">{used} / {member.monthly_credits || 0}</p>
                        </div>
                        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.06)' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${creditPercent}%`, background: creditPercent > 80 ? 'rgba(239, 68, 68, 0.8)' : creditPercent > 50 ? 'rgba(245, 158, 11, 0.8)' : 'rgba(34, 197, 94, 0.8)' }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-white/40">Projects</p>
                          <p className="text-xs text-white/40">{member.projectCount} / {member.max_projects != null ? member.max_projects : '∞'}</p>
                        </div>
                        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.06)' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${projPercent}%`, background: 'rgba(99, 102, 241, 0.8)' }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-white/40">Storage</p>
                          <p className="text-xs text-white/40">{member.saveCount} / {member.max_storage != null ? member.max_storage : '∞'}</p>
                        </div>
                        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.06)' }}>
                          <div className="h-full rounded-full transition-all" style={{ width: `${savePercent}%`, background: 'rgba(168, 85, 247, 0.8)' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Add Member Modal ── */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setShowAddModal(false); setAddForm({ username: '', password: '', confirmPassword: '', firstName: '', lastName: '', monthlyCredits: '', maxProjects: '', maxStorage: '' }); setError(''); setUsernameError(''); }} />
          <div className="relative w-full max-w-md glassmorphic-card rounded-[24px] p-6" style={{ border: '1px solid rgba(254,131,56,0.2)' }}>
            <button onClick={() => { setShowAddModal(false); setAddForm({ username: '', password: '', confirmPassword: '', firstName: '', lastName: '', monthlyCredits: '', maxProjects: '', maxStorage: '' }); setError(''); setUsernameError(''); }} className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors">
              <Icon name="X" size={20} />
            </button>
            <h3 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
              <Icon name="UserPlus" size={20} className="text-orange-400" />
              Add Team Member
            </h3>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1 custom-scrollbar">
              <InputField label="Username" value={addForm.username} onChange={(e) => {
                setAddForm({ ...addForm, username: e.target.value.replace(/\s/g, '') });
                if (usernameError) setUsernameError('');
              }} required />
              {usernameError && <p className="text-xs text-red-400 -mt-2">{usernameError}</p>}
              <div>
                <label className="block text-xs text-white/40 font-medium mb-1.5">Password <span className="text-red-400">*</span></label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input type={showAddPassword ? 'text' : 'password'} value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} autoComplete="new-password" className="w-full px-4 py-2.5 pr-10 rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none transition-all" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }} onFocus={(e) => { e.target.style.border = '1px solid rgba(254,131,56,0.4)'; }} onBlur={(e) => { e.target.style.border = '1px solid rgba(255,255,255,0.1)'; }} />
                    <button type="button" onClick={() => setShowAddPassword(!showAddPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors">
                      <Icon name={showAddPassword ? 'EyeOff' : 'Eye'} size={15} />
                    </button>
                  </div>
                  <button type="button" onClick={() => { const p = generatePassword(); setAddForm({ ...addForm, password: p, confirmPassword: p }); }} className="flex-shrink-0 p-2.5 rounded-xl text-white/40 hover:text-orange-400 transition-colors" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }} title="Generate password">
                    <Icon name="Shuffle" size={15} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 -mt-2">
                <p className={`text-xs flex items-center gap-1.5 ${addForm.password?.length >= 8 ? 'text-green-400' : 'text-gray-500'}`}>
                  <span>{addForm.password?.length >= 8 ? '✓' : '–'}</span> At least 8 characters
                </p>
                <p className={`text-xs flex items-center gap-1.5 ${/[A-Z]/.test(addForm.password) ? 'text-green-400' : 'text-gray-500'}`}>
                  <span>{/[A-Z]/.test(addForm.password) ? '✓' : '–'}</span> Uppercase (A–Z)
                </p>
                <p className={`text-xs flex items-center gap-1.5 ${/[a-z]/.test(addForm.password) ? 'text-green-400' : 'text-gray-500'}`}>
                  <span>{/[a-z]/.test(addForm.password) ? '✓' : '–'}</span> Lowercase (a–z)
                </p>
                <p className={`text-xs flex items-center gap-1.5 ${/[0-9]/.test(addForm.password) ? 'text-green-400' : 'text-gray-500'}`}>
                  <span>{/[0-9]/.test(addForm.password) ? '✓' : '–'}</span> Number (0–9)
                </p>
              </div>
              <div>
                <label className="block text-xs text-white/40 font-medium mb-1.5">Confirm Password <span className="text-red-400">*</span></label>
                <div className="relative">
                  <input type={showAddPassword ? 'text' : 'password'} value={addForm.confirmPassword} onChange={(e) => setAddForm({ ...addForm, confirmPassword: e.target.value })} autoComplete="new-password" className="w-full px-4 py-2.5 pr-10 rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none transition-all" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }} onFocus={(e) => { e.target.style.border = '1px solid rgba(254,131,56,0.4)'; }} onBlur={(e) => { e.target.style.border = '1px solid rgba(255,255,255,0.1)'; }} />
                  <button type="button" onClick={() => setShowAddPassword(!showAddPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors">
                    <Icon name={showAddPassword ? 'EyeOff' : 'Eye'} size={15} />
                  </button>
                </div>
              </div>
              {addForm.confirmPassword && addForm.password !== addForm.confirmPassword && (
                <p className="text-xs text-red-400 -mt-2">Passwords do not match</p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <InputField label="First Name" value={addForm.firstName} onChange={(e) => setAddForm({ ...addForm, firstName: e.target.value })} />
                <InputField label="Last Name" value={addForm.lastName} onChange={(e) => setAddForm({ ...addForm, lastName: e.target.value })} />
              </div>
              <div>
                <InputField label="Max Credits" value={addForm.monthlyCredits} onChange={(e) => setAddForm({ ...addForm, monthlyCredits: e.target.value })} type="number" required />
                <p className="text-xs text-white/30 mt-1 pl-1">Plan limit: {userProfile?.monthly_credits ?? 0} credits</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <InputField label="Max Projects" value={addForm.maxProjects} onChange={(e) => setAddForm({ ...addForm, maxProjects: e.target.value })} type="number" required />
                  <p className="text-xs text-white/30 mt-1 pl-1">Plan limit: {ownerMaxProjects ?? '∞'}</p>
                </div>
                <div>
                  <InputField label="Max Storage" value={addForm.maxStorage} onChange={(e) => setAddForm({ ...addForm, maxStorage: e.target.value })} type="number" required />
                  <p className="text-xs text-white/30 mt-1 pl-1">Plan limit: {ownerMaxSaves ?? '∞'}</p>
                </div>
              </div>
            </div>
            {error && <p className="text-red-400 text-xs mt-3">{error}</p>}
            <button
              onClick={handleAddMember}
              disabled={saving || !addForm.username?.trim() || !addForm.monthlyCredits || !addForm.maxProjects || !addForm.maxStorage || !validatePassword(addForm.password) || addForm.password !== addForm.confirmPassword}
              className="w-full mt-5 glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <><Icon name="Loader2" size={16} className="animate-spin" /> Creating...</> : 'Create Member'}
            </button>
          </div>
        </div>
      )}

      {/* ── Edit Member Modal ── */}
      {showEditModal && selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowEditModal(false)} />
          <div className="relative w-full max-w-md glassmorphic-card rounded-[24px] p-6" style={{ border: '1px solid rgba(254,131,56,0.2)' }}>
            <button onClick={() => setShowEditModal(false)} className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors">
              <Icon name="X" size={20} />
            </button>
            <h3 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
              <Icon name="Pencil" size={20} className="text-orange-400" />
              Edit Member
            </h3>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1 custom-scrollbar">
              <InputField label="Username" value={editForm.username} onChange={(e) => {
                setEditForm({ ...editForm, username: e.target.value.replace(/\s/g, '') });
                if (editUsernameError) setEditUsernameError('');
              }} />
              {editUsernameError && <p className="text-xs text-red-400 -mt-2">{editUsernameError}</p>}
              <div>
                <label className="block text-xs text-white/40 font-medium mb-1.5">New Password</label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input type={showEditPassword ? 'text' : 'password'} value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} autoComplete="new-password" className="w-full px-4 py-2.5 pr-10 rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none transition-all" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }} onFocus={(e) => { e.target.style.border = '1px solid rgba(254,131,56,0.4)'; }} onBlur={(e) => { e.target.style.border = '1px solid rgba(255,255,255,0.1)'; }} />
                    <button type="button" onClick={() => setShowEditPassword(!showEditPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors">
                      <Icon name={showEditPassword ? 'EyeOff' : 'Eye'} size={15} />
                    </button>
                  </div>
                  <button type="button" onClick={() => { const p = generatePassword(); setEditForm({ ...editForm, password: p, confirmPassword: p }); }} className="flex-shrink-0 p-2.5 rounded-xl text-white/40 hover:text-orange-400 transition-colors" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }} title="Generate password">
                    <Icon name="Shuffle" size={15} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 -mt-2">
                <p className={`text-xs flex items-center gap-1.5 ${editForm.password?.length >= 8 ? 'text-green-400' : 'text-gray-500'}`}>
                  <span>{editForm.password?.length >= 8 ? '✓' : '–'}</span> At least 8 characters
                </p>
                <p className={`text-xs flex items-center gap-1.5 ${/[A-Z]/.test(editForm.password) ? 'text-green-400' : 'text-gray-500'}`}>
                  <span>{/[A-Z]/.test(editForm.password) ? '✓' : '–'}</span> Uppercase (A–Z)
                </p>
                <p className={`text-xs flex items-center gap-1.5 ${/[a-z]/.test(editForm.password) ? 'text-green-400' : 'text-gray-500'}`}>
                  <span>{/[a-z]/.test(editForm.password) ? '✓' : '–'}</span> Lowercase (a–z)
                </p>
                <p className={`text-xs flex items-center gap-1.5 ${/[0-9]/.test(editForm.password) ? 'text-green-400' : 'text-gray-500'}`}>
                  <span>{/[0-9]/.test(editForm.password) ? '✓' : '–'}</span> Number (0–9)
                </p>
              </div>
              <div>
                <label className="block text-xs text-white/40 font-medium mb-1.5">Confirm Password</label>
                <div className="relative">
                  <input type={showEditPassword ? 'text' : 'password'} value={editForm.confirmPassword} onChange={(e) => setEditForm({ ...editForm, confirmPassword: e.target.value })} autoComplete="new-password" className="w-full px-4 py-2.5 pr-10 rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none transition-all" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }} onFocus={(e) => { e.target.style.border = '1px solid rgba(254,131,56,0.4)'; }} onBlur={(e) => { e.target.style.border = '1px solid rgba(255,255,255,0.1)'; }} />
                  <button type="button" onClick={() => setShowEditPassword(!showEditPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors">
                    <Icon name={showEditPassword ? 'EyeOff' : 'Eye'} size={15} />
                  </button>
                </div>
              </div>
              {editForm.confirmPassword && editForm.password !== editForm.confirmPassword && (
                <p className="text-xs text-red-400 -mt-2">Passwords do not match</p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <InputField label="First Name" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} />
                <InputField label="Last Name" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} />
              </div>
              <div>
                <InputField label="Max Credits" value={editForm.monthlyCredits} onChange={(e) => setEditForm({ ...editForm, monthlyCredits: e.target.value })} type="number" />
                <p className="text-xs text-white/30 mt-1 pl-1">Plan limit: {userProfile?.monthly_credits ?? 0} credits</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <InputField label="Max Projects" value={editForm.maxProjects} onChange={(e) => setEditForm({ ...editForm, maxProjects: e.target.value })} type="number" />
                  <p className="text-xs text-white/30 mt-1 pl-1">Plan limit: {ownerMaxProjects ?? '∞'}</p>
                </div>
                <div>
                  <InputField label="Max Storage" value={editForm.maxStorage} onChange={(e) => setEditForm({ ...editForm, maxStorage: e.target.value })} type="number" />
                  <p className="text-xs text-white/30 mt-1 pl-1">Plan limit: {ownerMaxSaves ?? '∞'}</p>
                </div>
              </div>
            </div>
            {error && <p className="text-red-400 text-xs mt-3">{error}</p>}
            <button
              onClick={handleEditMember}
              disabled={saving || (editForm.password && (!validatePassword(editForm.password) || editForm.password !== editForm.confirmPassword))}
              className="w-full mt-5 glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <><Icon name="Loader2" size={16} className="animate-spin" /> Saving...</> : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {showDeleteModal && selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowDeleteModal(false)} />
          <div className="relative w-full max-w-sm glassmorphic-card rounded-[24px] p-6" style={{ border: '1px solid rgba(239, 68, 68, 0.2)' }}>
            <div className="flex items-start gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                <Icon name="Trash2" size={22} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-1">Delete Member</h3>
                <p className="text-xs text-white/40">
                  Are you sure you want to remove <strong className="text-white">{getMemberName(selectedMember)}</strong> from your team? This will permanently delete their account and all data.
                </p>
              </div>
            </div>
            {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
            <div className="flex gap-3">
              <button
                onClick={handleDeleteMember}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white bg-red-500/20 border border-red-500/30 hover:bg-red-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? <><Icon name="Loader2" size={16} className="animate-spin" /> Deleting...</> : <><Icon name="Trash2" size={16} /> Delete</>}
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white/70 border border-white/10 hover:bg-white/5 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSection;
